/**
 * Product Card entry.
 *
 * Importing the SCSS here makes Vite bundle the card's CSS alongside its JS, so
 * the part ships as one unit and any block that declares `slate.uses` gets both.
 *
 * The card has no behaviour of its own — the name is a link, the bag is a link,
 * and everything else is CSS. What is here is its entrance: cards come in as
 * they are scrolled to, and the motion is CSS, so the global
 * prefers-reduced-motion guard in main.scss covers it without a second check.
 *
 * A card renders complete and in its end state. The attribute below is what
 * opts it into the entrance, so a card that is never armed — no
 * IntersectionObserver, or inserted by something this never hears about — is
 * simply visible rather than stuck at nothing.
 */

import './index.scss';

if ('IntersectionObserver' in window) {
    const revealer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    entry.target.setAttribute('data-reveal', 'in');
                    revealer.unobserve(entry.target);
                }
            }
        },
        { rootMargin: '0px 0px -10% 0px' }
    );

    /**
     * Put one card in its starting state and watch for it.
     *
     * @param {Element} card The card.
     */
    const arm = (card) => {
        if (card.hasAttribute('data-reveal')) {
            return;
        }

        card.setAttribute('data-reveal', 'pending');
        revealer.observe(card);
    };

    for (const card of document.querySelectorAll('.product-card')) {
        arm(card);
    }

    /*
     * Cards arrive after the page does. The shop rewrites the grid when a
     * filter changes and appends to it when the next page is fetched, and a
     * card that was not armed would come in already visible while the ones
     * beside it faded — or, worse, if this file hid them by default, not come
     * in at all.
     *
     * Watched from the document rather than from the grid, because the card
     * does not know what is holding it: it is drawn in the shop, in the
     * collection rows, and beside a product, and naming any of those here would
     * be this block knowing about another. childList only — attribute and text
     * changes never reach this — and the callback leaves immediately on the
     * mutations that carry no new nodes, which is most of them.
     */
    if ('MutationObserver' in window) {
        new MutationObserver((records) => {
            for (const record of records) {
                for (const node of record.addedNodes) {
                    if (Node.ELEMENT_NODE !== node.nodeType) {
                        continue;
                    }

                    if (node.matches('.product-card')) {
                        arm(node);
                    }

                    for (const card of node.querySelectorAll('.product-card')) {
                        arm(card);
                    }
                }
            }
        }).observe(document.body, { childList: true, subtree: true });
    }
}
